import { createSelector, Selector } from "@reduxjs/toolkit";

import { Task } from "../../../types/task";
import { isNot, isUndefined } from "../../../utilities/typeGuardUtils";
import { WIZARD_TASKS_FVMANIFEST_KEY } from "../../../utilities/variables/modulfConfigurationKeys";

import { ModulFState } from "../store";

import { FachverfahrenSlice, RuntimeFachverfahrenManifest } from "./types";

export const getFVManifestMap: Selector<ModulFState, FachverfahrenSlice["manifests"], never> = state =>
	state.fachverfahren.manifests;

export function getFVManifest(
	fvID: string | undefined
): Selector<ModulFState, RuntimeFachverfahrenManifest | undefined, never> {
	if (!fvID) {
		return () => undefined;
	}

	return state => state.fachverfahren.manifests[fvID];
}

export function getFVManifestVersionsById(fvID: string): Selector<ModulFState, number[], never> {
	return state =>
		state.fachverfahren.manifests[fvID]
			? Object.values(state.fachverfahren.manifests)
					.filter(
						manifest =>
							manifest?.identification.key === state.fachverfahren.manifests[fvID]?.identification.key
					)
					.flatMap(manifest => manifest?.identification.version ?? [])
			: [];
}

export const getFVManifests: Selector<ModulFState, RuntimeFachverfahrenManifest[], never> = createSelector(
	[getFVManifestMap],
	fvMap => Object.values(fvMap).filter(isNot(isUndefined))
);

export const getLatestFVManifests: Selector<ModulFState, RuntimeFachverfahrenManifest[], never> = createSelector(
	[getFVManifests],
	fvMap => {
		const latestVersions: Record<string, RuntimeFachverfahrenManifest> = {};

		fvMap.forEach(e => {
			if (e.identification.version > (latestVersions[e.identification.key]?.identification.version ?? 0)) {
				latestVersions[e.identification.key] = e;
			}
		});

		return Object.values(latestVersions);
	}
);

export function getLatestFVManifestByKey(
	fvKey: string
): Selector<ModulFState, RuntimeFachverfahrenManifest | undefined, never> {
	return createSelector([getFVManifests], fvMap => {
		const matchingManifests = fvMap.filter(e => e.identification.key === fvKey);

		if (matchingManifests.length === 0) {
			return undefined;
		}

		return matchingManifests.reduce((prev, next) =>
			prev.identification.version > next.identification.version ? prev : next
		);
	});
}

export function getAllFVTasks(fvID: string): Selector<ModulFState, Task[], never> {
	const fvManifest = getFVManifest(fvID);
	return function getAllFVTasksCreator(state) {
		const configValue = fvManifest(state)?.configurationValues.find(
			config => config.key === WIZARD_TASKS_FVMANIFEST_KEY
		)?.value;

		return configValue ? JSON.parse(configValue) : [];
	};
}
